import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormControl,
  RadioGroup,
  Radio,
  Autocomplete,
  Chip
} from '@mui/material';
import SecretField from 'components/JobList/CreateJob/SecretField';
import { useModalContext } from './ModalContext';

const clone = (x) => JSON.parse(JSON.stringify(x));

const objectPath = (o, fieldPath) => {
  let value = o;
  for (const field of fieldPath) {
    value = value?.[field];
  }
  return value;
};

const setObjectPath = (o, fieldPath, newValue) => {
  let value = o;
  for (const field of fieldPath.slice(0, fieldPath.length - 1)) {
    if (value[field] === undefined || value[field] === null) {
      value[field] = {};
    }
    value = value[field];
  }
  value[fieldPath[fieldPath.length - 1]] = newValue;
};

// Reads/writes a top-level field on formState.values (e.g. companyName)
export const useIntegrationGeneral = (fieldPath) => {
  const { formState, setFormState } = useModalContext();
  const value = objectPath(formState.values, fieldPath);
  const setField = (newValue) => {
    const updated = clone(formState);
    setObjectPath(updated.values, fieldPath, newValue);
    setFormState(updated);
  };
  return [value, setField];
};

// Reads/writes a path inside the JSON-string `config`/`source` fields on formState.values
const useJsonField = (jsonFieldName, fieldPath) => {
  const [raw, setRaw] = useIntegrationGeneral([jsonFieldName]);
  const parsed = raw ? JSON.parse(raw) : {};
  const value = objectPath(parsed, fieldPath);
  const setField = (newValue) => {
    const updated = clone(parsed);
    setObjectPath(updated, fieldPath, newValue);
    setRaw(JSON.stringify(updated, null, 2));
  };
  return [value, setField];
};

export const useIntegrationConfig = (fieldPath) =>
  useJsonField('config', fieldPath);
export const useIntegrationSource = (fieldPath) =>
  useJsonField('source', fieldPath);

export const SectionCard = ({ title, subheader, children }) => (
  <Card variant="outlined">
    <CardHeader
      subheader={subheader}
      subheaderTypographyProps={{
        sx: { color: 'primary.main', fontWeight: 500 }
      }}
      title={title}
      titleTypographyProps={{ sx: { fontWeight: 700 } }}
    />
    <Divider />
    <CardContent>{children}</CardContent>
  </Card>
);

export const IntegrationDetailsCard = () => {
  const { formState, isUpdate } = useModalContext();
  const [companyName, setCompanyName] = useIntegrationGeneral(['companyName']);

  return (
    <SectionCard
      subheader="Identifies this integration and the client it belongs to"
      title="Integration details"
    >
      <Grid container spacing={2}>
        {isUpdate && (
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Integration ID"
              value={formState.values.integrationId || ''}
              variant="standard"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            helperText="Enter company Shortname in Wurk"
            label="Company name"
            onChange={(e) => setCompanyName(e.target.value)}
            required
            value={companyName || ''}
            variant="standard"
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const AuthenticationCard = () => {
  const [authentication, setAuthentication] = useIntegrationConfig([
    'authentication'
  ]);
  const auth = authentication || {};
  const [mode, setMode] = React.useState(() =>
    auth?.type === '1password_item' ? 'onepassword' : 'direct'
  );

  const switchMode = (newMode) => {
    setMode(newMode);
    const preserved = { companyName: auth.companyName, isAdmin: true };
    if (newMode === 'onepassword') {
      setAuthentication({
        ...preserved,
        type: '1password_item',
        '1password_item_path': ''
      });
    } else {
      setAuthentication(preserved);
    }
  };

  const setAuthField = (field) => (value) =>
    setAuthentication({ ...auth, [field]: value });

  return (
    <SectionCard
      subheader="UKG credentials used to create the new hire"
      title="Authentication"
    >
      <FormControl sx={{ marginBottom: '16px' }}>
        <RadioGroup onChange={(e, value) => switchMode(value)} row value={mode}>
          <FormControlLabel
            control={<Radio />}
            label="Reference a 1Password item"
            value="onepassword"
          />
          <FormControlLabel
            control={<Radio />}
            label="Enter credentials directly"
            value="direct"
          />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            helperText="UKG company code, distinct from the client display name above"
            label="Wurk company/tenant name"
            onChange={(e) => setAuthField('companyName')(e.target.value)}
            value={auth.companyName || ''}
            variant="standard"
          />
        </Grid>

        {mode === 'onepassword' ? (
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="e.g. Client credentials/HCMImpSandbox"
              label="1Password item path"
              onChange={(e) =>
                setAuthField('1password_item_path')(e.target.value)
              }
              required
              value={auth['1password_item_path'] || ''}
              variant="standard"
            />
          </Grid>
        ) : (
          <>
            <Grid item md={6} xs={12}>
              <SecretField
                label="API key"
                secret={auth.apiKey ?? ''}
                setState={setAuthField('apiKey')}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Username"
                onChange={(e) => setAuthField('username')(e.target.value)}
                value={auth.username || ''}
                variant="standard"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <SecretField
                label="Password"
                secret={auth.password ?? ''}
                setState={setAuthField('password')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                helperText="Optional - only needed to override the default UKG API host"
                label="Base URL"
                onChange={(e) => setAuthField('baseUrl')(e.target.value)}
                value={auth.baseUrl || ''}
                variant="standard"
              />
            </Grid>
          </>
        )}
      </Grid>
    </SectionCard>
  );
};

export const EmployeeIdCounterCard = () => {
  const [lastId, setLastId] = useIntegrationConfig(['lastId']);
  return (
    <SectionCard
      subheader="Auto-increments on every processed hire"
      title="Employee ID Starting Number"
    >
      <TextField
        fullWidth
        helperText="Editing this on a live integration can cause employee ID collisions"
        label="Last ID"
        onChange={(e) =>
          setLastId(e.target.value === '' ? '' : Number(e.target.value))
        }
        type="number"
        value={lastId ?? ''}
        variant="standard"
      />
    </SectionCard>
  );
};

export const EmailNotificationsCard = ({
  urlPlaceholder = 'https://app.greenhouse.io/people/'
}) => {
  const [emailContacts, setEmailContacts] = useIntegrationConfig([
    'emailConfig',
    'emailContacts'
  ]);
  const [sendSuccessEmails, setSendSuccessEmails] = useIntegrationConfig([
    'emailConfig',
    'sendSuccessEmails'
  ]);
  const [greenhouseEmployeeUrl, setGreenhouseEmployeeUrl] =
    useIntegrationConfig(['emailConfig', 'greenhouseEmployeeUrl']);
  const contacts = emailContacts || [];

  return (
    <SectionCard
      subheader="Who gets notified when a hire is processed"
      title="Email notifications"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            multiple
            onChange={(e, newValue) => setEmailContacts(newValue)}
            options={[]}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                helperText="Press Enter after each address - leave blank for no notifications"
                label="Email recipients"
                placeholder="Add an email address"
                variant="standard"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  variant="outlined"
                  {...getTagProps({ index })}
                />
              ))
            }
            value={contacts}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            helperText="Prefixed to the employee ID to build the notification email's Greenhouse link"
            label="Greenhouse employee record URL"
            onChange={(e) => setGreenhouseEmployeeUrl(e.target.value)}
            placeholder={urlPlaceholder}
            value={greenhouseEmployeeUrl || ''}
            variant="standard"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendSuccessEmails === 'true'}
                onChange={(e, checked) =>
                  setSendSuccessEmails(checked ? 'true' : 'false')
                }
              />
            }
            label={
              <>
                Send on successful runs
                <FormHelperText>In addition to failures</FormHelperText>
              </>
            }
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};
